import {
  LuxAvatar2,
  LuxButton,
  LuxLoader,
  LuxTypography,
  Stack,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import useCustomTranslation from '@hooks/useCustomTranslation';
import useDoctors from '@hooks/useDoctors';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { useCallback, useMemo, useState } from 'react';
import { OurDoctorsModal } from './OurDoctorsModal';
import { TabletBreakpoint } from '@utils/consts/breakpoints.consts';
import { StepContainer } from '@components/Steps/Steps.styles';
import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';
import { DoctorDTO } from '@utils/interfaces';

/* type Doctor = {
  doctorId: string | number;
  avatar?: string;
  title: string;
  items?: { specialty?: string[] };
}; */

const MAX_AVATARS = 7;

export function DoctorsCard() {
  const practiceId = useOnlineBookingStore(state => state.practiceId);

  const theme = useTheme();
  const { t } = useCustomTranslation();
  const { data: doctors = [], isLoading } = useDoctors({
    practiceId,
    enabled: true,
  });
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorDTO | null>(null);
  const [learnMoreView, setLearnMoreView] = useState(false);
  const isMobile = useMediaQuery(`(max-width:${TabletBreakpoint}px)`);

  const allDoctors = useMemo(
    () => doctors.filter(doc => doc.isDoctor),
    [doctors],
  );

  const { visibleDoctors, tooMany, remaining } = useMemo(() => {
    const tooMany = allDoctors.length > MAX_AVATARS;
    const visibleDoctors = tooMany
      ? allDoctors.slice(0, MAX_AVATARS - 1)
      : allDoctors;
    const remaining = tooMany ? allDoctors.length - (MAX_AVATARS - 1) : 0;
    return { visibleDoctors, tooMany, remaining };
  }, [allDoctors]);

  const openModal = useCallback(() => {
    setIsOpenModal(true);
    setLearnMoreView(false);
    setSelectedDoctor(null);
  }, []);

  const openModalWithDoctor = useCallback((doctor: DoctorDTO) => {
    setIsOpenModal(true);
    setSelectedDoctor(doctor);
    setLearnMoreView(true);
  }, []);
  const closeModal = useCallback(() => setIsOpenModal(false), []);

  const handleDoctorKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, doctor: DoctorDTO) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openModalWithDoctor(doctor);
      }
    },
    [openModalWithDoctor],
  );

  const handleBackFromDoctor = () => {
    setSelectedDoctor(null);
    setLearnMoreView(false);
  };

  const renderDoctor = useCallback(
    (doc: DoctorDTO) => (
      <Stack
        key={doc.doctorId}
        flexDirection="column"
        gap="1rem"
        width="100%"
        alignItems="center"
        sx={{ cursor: 'pointer' }}
        onClick={() => openModalWithDoctor(doc)}
        onKeyDown={event => handleDoctorKeyDown(event, doc)}
        role="button"
        tabIndex={0}
        aria-label={`${t('practiceCard.viewDoctorDetails')} ${doc.title}`}
      >
        <LuxAvatar2
          size="x-large"
          src={doc.avatar}
          firstName={doc.firstName}
          variant={doc.avatar ? 'image' : 'initials'}
          lastName={doc.lastName}
          sx={{
            '& img': {
              objectFit: 'cover',
              objectPosition: 'center',
            },
          }}
        />
        <Stack
          flexDirection="column"
          gap="0.5rem"
          width="100%"
          alignItems="center"
        >
          <LuxTypography
            variant="textMedium"
            fontWeightBold
            sx={{ whiteSpace: isMobile ? 'nowrap' : 'normal' }}
          >
            {doc.title}
          </LuxTypography>
          <LuxTypography
            variant="textMdRegular"
            color={theme.palette.LUX.obTokens.textColorBaseSecondary}
            sx={{ whiteSpace: isMobile ? 'nowrap' : 'normal' }}
          >
            {doc.jobTitle || ''}
          </LuxTypography>
        </Stack>
      </Stack>
    ),
    [
      handleDoctorKeyDown,
      isMobile,
      openModalWithDoctor,
      t,
      theme.palette.LUX.obTokens.textColorBaseSecondary,
    ],
  );

  return (
    allDoctors?.length > 0 && (
      <StepContainer>
        <CardTitleWrapper
          borderColor={theme.palette.LUX.obTokens.bordersColorNeutralSecondary}
        >
          <LuxTypography variant="heading4">
            {t('practiceCard.ourDoctors')}
          </LuxTypography>

          {!isMobile && (
            <LuxButton onClick={openModal} variant="ghost" size="small">
              {t('practiceCard.viewAll')}
            </LuxButton>
          )}
        </CardTitleWrapper>

        <Stack padding="0.5rem 1rem 1rem" gap="2rem">
          <Stack
            flexDirection="row"
            gap="1.5rem"
            width="100%"
            justifyContent={isMobile ? 'flex-start' : 'center'}
            sx={{
              overflowX: isMobile ? 'auto' : 'visible',
            }}
          >
            {isLoading ? (
              <LuxLoader size="large" />
            ) : (
              <>
                {visibleDoctors.map(renderDoctor)}

                {tooMany && (
                  <Stack
                    flexDirection="column"
                    gap="1rem"
                    width="100%"
                    alignItems="center"
                    sx={{ cursor: 'pointer' }}
                    onClick={openModal}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openModal();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`${t('practiceCard.viewAll')} ${t(
                      'practiceCard.ourDoctors',
                    )}`}
                  >
                    <LuxAvatar2
                      size="x-large"
                      variant="generic"
                      sx={{
                        '& img': {
                          objectFit: 'cover',
                          objectPosition: 'center',
                        },
                      }}
                    >
                      +{remaining}
                    </LuxAvatar2>
                  </Stack>
                )}
              </>
            )}
          </Stack>

          {isMobile && (
            <LuxButton onClick={openModal} variant="secondary">
              {t('practiceCard.viewAll')}
            </LuxButton>
          )}
        </Stack>

        <OurDoctorsModal
          isOpen={isOpenModal}
          doctors={allDoctors}
          onClose={closeModal}
          isLoadingDoctors={isLoading}
          initialDoctor={selectedDoctor}
          initialLearnMoreView={learnMoreView}
          onBack={handleBackFromDoctor}
        />
      </StepContainer>
    )
  );
}
